import { useCallback, useState } from "react";

import { Check, Copy, KeyRound } from "lucide-react";

import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";

interface CopyableSecretProps {
  /** El TOTP secret. Se muestra agrupado de a 4 caracteres para legibilidad. */
  value: string;
  /** Label corta sobre el campo. */
  label?: string;
  className?: string;
}

/**
 * Campo mono con copy-to-clipboard. Feedback de dos canales (icono +
 * toast) porque el usuario puede no ver el icono si clickea rápido y
 * sigue tipeando el QR en su app.
 *
 * Formato: agrupa el secret en bloques de 4 chars con un espacio no
 * breaking — es solo visual, el value real se mantiene y se copia sin
 * espacios.
 */
export function CopyableSecret({ value, label = "Código manual", className }: CopyableSecretProps) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Código copiado al portapapeles");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar. Cópialo manualmente.");
    }
  }, [value]);

  // Agrupa visualmente de 4 en 4 — "ABCD EFGH IJKL ..." — con espacio
  // tipográfico fino para que no se vea pesado.
  const formatted = value.replace(/(.{4})/g, "$1 ").trim();

  return (
    <div className={cn("w-full", className)}>
      {label ? (
        <p className="mb-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </p>
      ) : null}

      <div
        className={cn(
          "group flex items-center gap-2.5 rounded-lg border bg-background py-1 pl-3 pr-1 transition-colors",
          "focus-within:border-ring focus-within:shadow-ring-focus",
        )}
      >
        <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <code
          className="flex-1 select-all break-all font-mono text-[13px] font-medium tracking-wider text-foreground"
          aria-label="Código secreto para autenticador"
        >
          {formatted}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? "Copiado" : "Copiar código"}
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {copied ? (
            <Check className="h-4 w-4 animate-pulse-success text-primary" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
